import { db } from '../firebase';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  Timestamp,
  setDoc,
} from 'firebase/firestore';
import { DateTime } from 'luxon';

const ATTENDANCE_CALC_VERSION = '2026-02-25.v1-timecards';
const MAX_SHIFT_MINUTES = 16 * 60;

// Read attendance thresholds from tenants/{tenantId}/settings/attendance (with safe defaults)
export async function getAttendanceThresholds(tenantId) {
  try {
    if (!tenantId) return { tardyThresholdMinutes: 0, earlyLeaveThresholdMinutes: 0, earlyOutThresholdMinutes: 0 };
    const docRef = doc(db, `tenants/${tenantId}/settings`, 'attendance');
    const snap = await getDoc(docRef);
    if (!snap.exists()) return { tardyThresholdMinutes: 0, earlyLeaveThresholdMinutes: 0, earlyOutThresholdMinutes: 0 };
    const data = snap.data() || {};
    return {
      tardyThresholdMinutes: Number(data.tardyThresholdMinutes || 0) || 0,
      earlyLeaveThresholdMinutes: Number(data.earlyLeaveThresholdMinutes || 0) || 0,
      earlyOutThresholdMinutes: Number(data.earlyOutThresholdMinutes || data.earlyLeaveThresholdMinutes || 0) || 0,
    };
  } catch (err) {
    console.warn('getAttendanceThresholds error', err);
    return { tardyThresholdMinutes: 0, earlyLeaveThresholdMinutes: 0, earlyOutThresholdMinutes: 0 };
  }
}

function toDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toPositiveMinutes(valueMs) {
  return Math.max(0, Math.floor(valueMs / 60000));
}

function normalizeTimeString(timeLike, fallback = '00:00') {
  if (!timeLike || typeof timeLike !== 'string') return fallback;
  const parts = timeLike.split(':').map(Number);
  if (!Number.isFinite(parts[0])) return fallback;
  const hh = String(Math.max(0, Math.min(23, parts[0]))).padStart(2, '0');
  const mm = String(Math.max(0, Math.min(59, Number.isFinite(parts[1]) ? parts[1] : 0))).padStart(2, '0');
  return `${hh}:${mm}`;
}

function resolveLocationTimezone(shift) {
  const zone =
    shift?.locationTimezone ||
    shift?.timeZone ||
    shift?.timezone ||
    shift?.location?.timeZone ||
    shift?.location?.timezone;
  return zone && typeof zone === 'string' && zone.includes('/') ? zone : 'America/Chicago';
}

function extractCanonicalWindowFromTimestamps(shift) {
  const start = toDate(shift?.startAt);
  const end = toDate(shift?.endAt);
  if (!start || !end || end <= start) return null;
  return {
    scheduledStart: start,
    scheduledEnd: end,
    localDateKey: DateTime.fromJSDate(start, { zone: resolveLocationTimezone(shift) }).toFormat('yyyy-MM-dd'),
  };
}

function resolveLocalDateKey(shift, zone) {
  const candidate = shift?.localDateKey || shift?.shiftDateKey || shift?.date || shift?.shiftDate;
  if (!candidate) return null;

  if (typeof candidate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
    return candidate;
  }

  const d = toDate(candidate);
  if (!d) return null;
  return DateTime.fromJSDate(d, { zone }).toFormat('yyyy-MM-dd');
}

function buildScheduledWindow(shift, zone, localDateKeyOverride = null) {
  const canonical = extractCanonicalWindowFromTimestamps(shift);
  if (canonical) return canonical;

  const startTime = normalizeTimeString(shift?.shiftStartTime || shift?.startTime || shift?.shiftStart || shift?.start, '00:00');
  const endTime = normalizeTimeString(shift?.shiftEndTime || shift?.endTime || shift?.shiftEnd || shift?.end, startTime);
  const localDateKey = localDateKeyOverride || resolveLocalDateKey(shift, zone);
  if (!localDateKey) return null;

  const scheduledStart = DateTime.fromISO(`${localDateKey}T${startTime}:00`, { zone });
  let scheduledEnd = DateTime.fromISO(`${localDateKey}T${endTime}:00`, { zone });
  if (!scheduledStart.isValid || !scheduledEnd.isValid) return null;

  // Overnight shift support.
  if (scheduledEnd <= scheduledStart) {
    scheduledEnd = scheduledEnd.plus({ days: 1 });
  }

  return {
    scheduledStart: scheduledStart.toJSDate(),
    scheduledEnd: scheduledEnd.toJSDate(),
    localDateKey,
  };
}

function pickBestWindow(shift, zone, earliestIn, latestOut) {
  const primary = buildScheduledWindow(shift, zone);
  const anchor = latestOut || earliestIn || null;
  if (!anchor) return primary;

  // If shift date metadata is off, align to punch local date when it is clearly a better fit.
  const punchDateKey = DateTime.fromJSDate(anchor, { zone }).toFormat('yyyy-MM-dd');
  const punchAligned = buildScheduledWindow(shift, zone, punchDateKey);
  if (!primary) return punchAligned;
  if (!punchAligned) return primary;

  const primaryDist = Math.abs(primary.scheduledEnd.getTime() - anchor.getTime());
  const punchDist = Math.abs(punchAligned.scheduledEnd.getTime() - anchor.getTime());
  const suspicious = primaryDist > 18 * 60 * 60000;

  if (suspicious && punchDist < primaryDist) {
    return punchAligned;
  }
  return primary;
}

function pickPunches(timeEntriesForShift) {
  const active = (timeEntriesForShift || []).filter((e) => e && e.status !== 'voided');
  const clockIns = active
    .filter((e) => e.type === 'clock_in')
    .map((e) => toDate(e.timestamp))
    .filter(Boolean)
    .sort((a, b) => a - b);
  const clockOuts = active
    .filter((e) => e.type === 'clock_out')
    .map((e) => toDate(e.timestamp))
    .filter(Boolean)
    .sort((a, b) => a - b);

  return {
    earliestIn: clockIns.length ? clockIns[0] : null,
    latestOut: clockOuts.length ? clockOuts[clockOuts.length - 1] : null,
    clockInCount: clockIns.length,
    clockOutCount: clockOuts.length,
  };
}

// Compute attendance fields in one place so all writers stay consistent.
export function computeAttendanceFlags(shift, timeEntriesForShift = [], thresholds = {}) {
  const tardyThresholdMinutes = Number(thresholds?.tardyThresholdMinutes || 0) || 0;
  const earlyOutThresholdMinutes = Number(
    (typeof thresholds?.earlyOutThresholdMinutes !== 'undefined'
      ? thresholds?.earlyOutThresholdMinutes
      : thresholds?.earlyLeaveThresholdMinutes) || 0
  ) || 0;

  const zone = resolveLocationTimezone(shift);
  const punches = pickPunches(timeEntriesForShift);
  const { earliestIn, latestOut, clockInCount, clockOutCount } = punches;
  const window = pickBestWindow(shift, zone, earliestIn, latestOut);
  const wasAbsent = !earliestIn && !latestOut;

  // Safe defaults if the shift has bad/missing scheduling fields.
  if (!window) {
    const attendanceStatus = wasAbsent ? 'Absent' : 'On Time';
    return {
      window: null,
      earliestIn,
      latestOut,
      clockInCount,
      clockOutCount,
      attendanceStatus,
      wasTardy: false,
      leftEarly: false,
      wasAbsent,
      tardyMinutes: 0,
      earlyOutMinutes: 0,
      workedMinutes: 0,
      hoursMissed: 0,
      breakMinutes: Number(shift?.breakMinutes || 0) || 0,
      roundedMinutes: 0,
      regularMinutes: 0,
      overtimeMinutes: 0,
      exceptionFlags: wasAbsent ? ['MISSING_CLOCK_OUT'] : [],
      hasExceptions: wasAbsent,
      attendanceCalcVersion: ATTENDANCE_CALC_VERSION,
      evaluatedAt: Timestamp.now(),
    };
  }

  const scheduledMinutes = toPositiveMinutes(window.scheduledEnd.getTime() - window.scheduledStart.getTime());
  const breakMinutes = Number(shift?.breakMinutes || 0) || 0;

  const tardyMinutes = earliestIn ? toPositiveMinutes(earliestIn.getTime() - window.scheduledStart.getTime()) : 0;
  const earlyOutMinutes = latestOut ? toPositiveMinutes(window.scheduledEnd.getTime() - latestOut.getTime()) : 0;
  const grossWorkedMinutes = earliestIn && latestOut ? toPositiveMinutes(latestOut.getTime() - earliestIn.getTime()) : 0;
  const rawWorkedMinutes = earliestIn && latestOut ? Math.floor((latestOut.getTime() - earliestIn.getTime()) / 60000) : 0;
  const workedMinutes = Math.max(0, grossWorkedMinutes - breakMinutes);
  const hoursMissed = Math.max(0, Number(((Math.max(0, scheduledMinutes - workedMinutes)) / 60).toFixed(2)));

  const wasTardy = tardyMinutes > tardyThresholdMinutes;
  const leftEarly = earlyOutMinutes > earlyOutThresholdMinutes;

  let attendanceStatus = 'On Time';
  if (wasAbsent) attendanceStatus = 'Absent';
  else if (wasTardy && leftEarly) attendanceStatus = 'Tardy & Left Early';
  else if (wasTardy) attendanceStatus = 'Tardy';
  else if (leftEarly) attendanceStatus = 'Left Early';

  const overtimeMinutes = Math.max(0, workedMinutes - 2400);
  const regularMinutes = Math.max(0, workedMinutes - overtimeMinutes);
  const exceptionFlags = [];
  if (clockInCount === 0 || clockOutCount === 0) exceptionFlags.push('MISSING_CLOCK_OUT');
  if (clockInCount > 1) exceptionFlags.push('MULTIPLE_CLOCK_INS');
  if (clockOutCount > 1) exceptionFlags.push('MULTIPLE_CLOCK_OUTS');
  if (leftEarly) exceptionFlags.push('EARLY_OUT');
  if (wasTardy) exceptionFlags.push('LATE');
  if (breakMinutes < 0 || breakMinutes > grossWorkedMinutes) exceptionFlags.push('BREAK_VIOLATION');
  if (rawWorkedMinutes < 0) exceptionFlags.push('NEGATIVE_DURATION');
  if (grossWorkedMinutes > MAX_SHIFT_MINUTES || workedMinutes > MAX_SHIFT_MINUTES) exceptionFlags.push('SHIFT_TOO_LONG');
  if (overtimeMinutes > 0) exceptionFlags.push('OVERTIME_CREATED');
  const hasExceptions = exceptionFlags.length > 0;

  return {
    window,
    earliestIn,
    latestOut,
    clockInCount,
    clockOutCount,
    attendanceStatus,
    wasTardy,
    leftEarly,
    wasAbsent,
    tardyMinutes,
    earlyOutMinutes,
    workedMinutes,
    hoursMissed,
    breakMinutes,
    roundedMinutes: workedMinutes,
    regularMinutes,
    overtimeMinutes,
    exceptionFlags,
    hasExceptions,
    attendanceCalcVersion: ATTENDANCE_CALC_VERSION,
    evaluatedAt: Timestamp.now(),
  };
}

function buildTimecardId(shiftId) {
  return `shift_${shiftId}`;
}

function normalizePayrollCode(raw) {
  return String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function fallbackPayrollCode(roleName, roleId) {
  const words = String(roleName || '').toUpperCase().match(/[A-Z0-9]+/g) || [];
  let candidate = words.map((w) => w.slice(0, 1)).join('');
  if (candidate.length < 3) candidate = words.join('');
  if (candidate.length < 3) candidate = String(roleId || 'ROLE').toUpperCase().replace(/[^A-Z0-9]/g, '');
  candidate = candidate.slice(0, 5);
  if (candidate.length < 3) candidate = candidate.padEnd(3, 'X');
  return candidate;
}

function asNumberOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

async function resolveRoleAndRateSnapshots(tenantId, shift, existing) {
  const roleId = shift.roleId || shift.role || existing?.roleId || existing?.shiftSnapshot?.roleId || null;
  const locationId = shift.locationId || existing?.locationId || existing?.shiftSnapshot?.locationId || null;
  const employeeId = shift.employeeId || existing?.employeeId || existing?.shiftSnapshot?.employeeId || null;

  let roleName = String(
    shift.roleName ||
    existing?.roleNameSnapshot ||
    existing?.roleName ||
    existing?.shiftSnapshot?.roleName ||
    ''
  ).trim() || null;

  let rolePayrollCode = normalizePayrollCode(
    existing?.rolePayrollCodeSnapshot ||
    existing?.rolePayrollCode ||
    existing?.shiftSnapshot?.rolePayrollCode
  );

  let hourlyRateSnapshot = asNumberOrNull(existing?.hourlyRateSnapshot);
  let roleDoc = null;

  if (roleId && (!roleName || !rolePayrollCode || hourlyRateSnapshot === null)) {
    const roleSnap = await getDoc(doc(db, `tenants/${tenantId}/roles`, roleId));
    roleDoc = roleSnap.exists() ? (roleSnap.data() || {}) : null;
    if (!roleName && roleDoc?.name) roleName = String(roleDoc.name);
    if (!rolePayrollCode && roleDoc?.rolePayrollCode) {
      rolePayrollCode = normalizePayrollCode(roleDoc.rolePayrollCode);
    }
  }

  if (hourlyRateSnapshot === null && employeeId && locationId && roleId) {
    const assignmentId = `${locationId}_${roleId}`;
    const assignmentSnap = await getDoc(doc(db, `tenants/${tenantId}/employees/${employeeId}/assignments`, assignmentId));
    if (assignmentSnap.exists()) {
      const assignment = assignmentSnap.data() || {};
      const wage = asNumberOrNull(assignment.wage);
      if (wage !== null) hourlyRateSnapshot = wage;
    }
  }

  if (hourlyRateSnapshot === null && employeeId && roleId) {
    const empSnap = await getDoc(doc(db, `tenants/${tenantId}/employees`, employeeId));
    if (empSnap.exists()) {
      const emp = empSnap.data() || {};
      if (emp?.rates && Object.prototype.hasOwnProperty.call(emp.rates, roleId)) {
        const mappedRate = asNumberOrNull(emp.rates[roleId]);
        if (mappedRate !== null) hourlyRateSnapshot = mappedRate;
      }
      if (hourlyRateSnapshot === null) {
        const hourlyRate = asNumberOrNull(emp.hourlyRate);
        if (hourlyRate !== null) hourlyRateSnapshot = hourlyRate;
      }
    }
  }

  if (hourlyRateSnapshot === null && roleDoc) {
    const fallbackRate = asNumberOrNull(roleDoc.defaultWage ?? roleDoc.wage ?? roleDoc.rate);
    if (fallbackRate !== null) hourlyRateSnapshot = fallbackRate;
  }

  if (!rolePayrollCode) {
    rolePayrollCode = fallbackPayrollCode(roleName || roleId || 'ROLE', roleId || 'ROLE');
  }

  return {
    roleId: roleId ? String(roleId) : null,
    roleName: roleName ? String(roleName) : null,
    rolePayrollCode: rolePayrollCode || null,
    hourlyRateSnapshot,
  };
}

// Writes payroll truth to timecards (does not mutate shift attendance fields).
export async function updateShiftTimecard(tenantId, shiftId) {
  try {
    if (!tenantId || !shiftId) return;

    const shiftRef = doc(db, `tenants/${tenantId}/shifts`, shiftId);
    const shiftSnap = await getDoc(shiftRef);
    if (!shiftSnap.exists()) return;
    const shift = { id: shiftSnap.id, ...shiftSnap.data() };

    const zone = resolveLocationTimezone(shift);
    const window = buildScheduledWindow(shift, zone);

    // Fetch time entries that reference this shiftId first.
    const entriesQ = query(collection(db, `tenants/${tenantId}/timeEntries`), where('shiftId', '==', shiftId));
    const entriesSnap = await getDocs(entriesQ);
    const entries = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((e) => e.status !== 'voided');

    // Fallback: if not explicitly linked, gather all employee entries during the shift's local day.
    let timeEntriesForShift = entries;
    if (!timeEntriesForShift.length && shift.employeeId && window) {
      const dayStart = DateTime.fromISO(`${window.localDateKey}T00:00:00`, { zone }).toJSDate();
      const dayEnd = DateTime.fromISO(`${window.localDateKey}T23:59:59.999`, { zone }).toJSDate();
      if (dayStart && dayEnd) {
        const allEntriesQ = query(collection(db, `tenants/${tenantId}/timeEntries`), where('employeeId', '==', shift.employeeId));
        const allSnap = await getDocs(allEntriesQ);
        const allEntries = allSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((e) => e.status !== 'voided');
        timeEntriesForShift = allEntries.filter((e) => {
          const ts = toDate(e.timestamp);
          return ts && ts >= dayStart && ts <= dayEnd;
        });
      }
    }

    const thresholds = await getAttendanceThresholds(tenantId);
    const flags = computeAttendanceFlags(shift, timeEntriesForShift, thresholds);
    const businessDateKey =
      flags.window?.localDateKey ||
      shift.localDateKey ||
      shift.shiftDateKey ||
      resolveLocalDateKey(shift, zone) ||
      null;

    const timecardRef = doc(db, `tenants/${tenantId}/timecards`, buildTimecardId(shiftId));
    const existingSnap = await getDoc(timecardRef);
    const existing = existingSnap.exists() ? existingSnap.data() || {} : {};
    if (existing.payrollLocked === true || existing.locked === true) return;
    const snapshots = await resolveRoleAndRateSnapshots(tenantId, shift, existing);

    const payload = {
      tenantId,
      shiftId,
      employeeId: shift.employeeId || null,
      employeeName: shift.employeeName || null,
      locationId: shift.locationId || null,
      locationTimezone: zone,
      roleId: snapshots.roleId,
      roleName: snapshots.roleName,
      roleNameSnapshot: existing.roleNameSnapshot || snapshots.roleName || null,
      rolePayrollCode: snapshots.rolePayrollCode,
      rolePayrollCodeSnapshot: existing.rolePayrollCodeSnapshot || snapshots.rolePayrollCode || null,
      hourlyRateSnapshot: existing.hourlyRateSnapshot ?? snapshots.hourlyRateSnapshot ?? null,
      currency: existing.currency || 'USD',
      businessDateKey,
      localDateKey: businessDateKey,
      shiftDateKey: shift.shiftDateKey || businessDateKey || null,
      shiftSnapshot: {
        shiftId,
        employeeId: shift.employeeId || null,
        locationId: shift.locationId || null,
        roleId: snapshots.roleId,
        roleName: snapshots.roleName,
        rolePayrollCode: existing.rolePayrollCodeSnapshot || snapshots.rolePayrollCode || null,
        hourlyRateSnapshot: existing.hourlyRateSnapshot ?? snapshots.hourlyRateSnapshot ?? null,
        shiftStartTime: shift.shiftStartTime || null,
        shiftEndTime: shift.shiftEndTime || null,
        breakMinutes: Number(shift.breakMinutes || 0) || 0,
        localDateKey: shift.localDateKey || shift.shiftDateKey || businessDateKey || null,
      },
      scheduledStartAt: flags.window?.scheduledStart ? Timestamp.fromDate(flags.window.scheduledStart) : null,
      scheduledEndAt: flags.window?.scheduledEnd ? Timestamp.fromDate(flags.window.scheduledEnd) : null,
      firstClockInAt: flags.earliestIn ? Timestamp.fromDate(flags.earliestIn) : null,
      lastClockOutAt: flags.latestOut ? Timestamp.fromDate(flags.latestOut) : null,
      clockInCount: flags.clockInCount,
      clockOutCount: flags.clockOutCount,
      timeEntryIds: timeEntriesForShift.map((e) => String(e.id)).filter(Boolean),
      attendanceStatus: flags.attendanceStatus,
      wasTardy: flags.wasTardy,
      leftEarly: flags.leftEarly,
      wasAbsent: flags.wasAbsent,
      tardyMinutes: flags.tardyMinutes,
      earlyOutMinutes: flags.earlyOutMinutes,
      workedMinutes: flags.workedMinutes,
      hoursMissed: flags.hoursMissed,
      breakMinutes: flags.breakMinutes,
      roundedMinutes: flags.roundedMinutes,
      regularMinutes: flags.regularMinutes,
      overtimeMinutes: flags.overtimeMinutes,
      exceptionFlags: flags.exceptionFlags,
      hasExceptions: flags.hasExceptions,
      approved: existing.approved === true,
      approvedBy: existing.approvedBy || null,
      approvedAt: existing.approvedAt || null,
      finalWorkedMinutes: existing.finalWorkedMinutes ?? null,
      finalBreakMinutes: existing.finalBreakMinutes ?? null,
      finalRegularMinutes: existing.finalRegularMinutes ?? null,
      finalOvertimeMinutes: existing.finalOvertimeMinutes ?? null,
      finalRoundedMinutes: existing.finalRoundedMinutes ?? null,
      finalizedAt: existing.finalizedAt || null,
      finalizedBy: existing.finalizedBy || null,
      locked: existing.locked === true || existing.payrollLocked === true,
      payrollLocked: existing.payrollLocked === true,
      attendanceCalcVersion: flags.attendanceCalcVersion,
      attendanceLastWriter: 'client.updateShiftTimecard',
      evaluatedAt: flags.evaluatedAt,
      updatedAt: Timestamp.now(),
    };

    if (!existingSnap.exists()) {
      payload.createdAt = Timestamp.now();
    }

    await setDoc(timecardRef, payload, { merge: true });
  } catch (err) {
    console.error('updateShiftTimecard error', err);
  }
}

// Legacy alias retained for compatibility while call sites migrate.
export const updateShiftAttendance = updateShiftTimecard;
